export interface ZoomControls {
  zoomIn: () => void;
  zoomOut: () => void;
  fitPage: () => void;
  /** Relative zoom around a point in the element's coordinates. */
  zoomBy: (delta: number, center: { x: number; y: number }) => void;
}

const MAX_WHEEL_STEP = 0.25;

/**
 * Route the browser's zoom gestures to the PDF while the pointer is over it:
 * Ctrl/Cmd + wheel (and trackpad pinch, which arrives the same way) zooms the
 * document under the cursor; Ctrl/Cmd + `=`, `-`, `0` zoom in, out and fit the
 * page. The wheel listener must be non-passive to cancel the browser's zoom.
 */
export function attachZoomShortcuts(
  el: HTMLElement,
  zoom: ZoomControls
): () => void {
  const onWheel = (e: WheelEvent) => {
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    const rect = el.getBoundingClientRect();
    const step = Math.min(Math.abs(e.deltaY) * 0.002, MAX_WHEEL_STEP);
    zoom.zoomBy(-Math.sign(e.deltaY) * step, {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };
  const onKeyDown = (e: KeyboardEvent) => {
    if (!e.ctrlKey && !e.metaKey) return;
    if (!el.matches(':hover')) return;
    const action = {
      '=': zoom.zoomIn,
      '+': zoom.zoomIn,
      '-': zoom.zoomOut,
      '0': zoom.fitPage,
    }[e.key];
    if (!action) return;
    e.preventDefault();
    action();
  };
  el.addEventListener('wheel', onWheel, { passive: false });
  document.addEventListener('keydown', onKeyDown);
  return () => {
    el.removeEventListener('wheel', onWheel);
    document.removeEventListener('keydown', onKeyDown);
  };
}
